import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import JsonLd from "@/components/JsonLd";
import { useUpcomingEvents } from "@/lib/useUpcomingEvents";
import { GHL_SOCIAL_FORM_URL } from "@/seo/pages";
import { eventSchema } from "@/seo/schema";
import "./events.css";
import { dateParts, prettyDate } from "./eventUtils";
import { useFadeUp } from "./useFadeUp";

const EventsList = () => {
  const { events, loading } = useUpcomingEvents();
  const ref = useFadeUp(loading ? "loading" : `n-${events.length}`);

  return (
    <div className="sm-events" ref={ref} data-testid="events-list-root">
      <SEO path="/events" />
      {events.map((ev) => (
        <JsonLd key={ev.id} data={eventSchema(ev)} />
      ))}

      {/* Hero */}
      <section className="sm-dark sm-section" data-testid="events-hero">
        <div className="max-w-[1240px] mx-auto">
          <p className="sm-eyebrow sm-fade" data-testid="events-eyebrow">
            In-Person · Healing Socials
          </p>
          <h1
            className="sm-headline sm-fade mt-6"
            style={{ fontSize: "clamp(2.6rem, 6vw, 5.4rem)" }}
            data-testid="events-headline"
          >
            An evening<br />
            <span className="sm-city">in the circle.</span>
          </h1>
          <p className="sm-fade mt-10 max-w-xl" style={{ fontSize: "1.05rem" }}>
            A complimentary gathering. We open the night in blessing, share
            the lineage, and offer healings from the Shiva Murti team. Space
            is intentionally small.
          </p>
        </div>
      </section>

      <div className="sm-poleng" aria-hidden="true" />

      {/* Upcoming grid */}
      <section className="sm-light sm-section" data-testid="events-upcoming">
        <div className="max-w-[1240px] mx-auto">
          <div className="sm-fade flex items-end justify-between flex-wrap gap-6">
            <div>
              <p className="sm-eyebrow">Upcoming</p>
              <h2
                className="sm-headline mt-4"
                style={{ fontSize: "clamp(2rem, 4vw, 3.4rem)" }}
              >
                Where we gather next.
              </h2>
            </div>
            <p className="sm-label" style={{ opacity: 0.7 }}>
              {loading ? "Loading…" : `${events.length} upcoming`}
            </p>
          </div>

          <hr className="sm-rule mt-10" />

          {!loading && events.length === 0 && (
            <div className="sm-fade mt-20 text-center" data-testid="events-empty">
              <p className="sm-label" style={{ opacity: 0.7 }}>No gatherings scheduled</p>
              <p className="mt-6 max-w-md mx-auto" style={{ fontSize: "1.05rem" }}>
                The next Healing Social will be announced here soon. Write to
                us to be told first.
              </p>
              <Link to="/contact" className="sm-btn sm-btn-ghost mt-10" data-testid="events-contact-cta">
                Write to us
                <span aria-hidden>→</span>
              </Link>
            </div>
          )}

          {events.length > 0 && (
            <div
              className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10"
              data-testid="events-grid"
            >
              {events.map((ev) => {
                const dp = dateParts(ev.date);
                return (
                  <article
                    key={ev.id}
                    className="sm-card sm-fade"
                    data-testid={`event-card-${ev.slug}`}
                  >
                    <div className="flex items-start gap-5">
                      <div className="sm-datecard">
                        <span className="m">{dp.month}</span>
                        <span className="d">{dp.day}</span>
                        <span className="w">{dp.weekdayShort}</span>
                      </div>
                      <div className="flex-1">
                        <p className="sm-eyebrow">In-Person · {dp.year}</p>
                        <h3
                          className="sm-headline mt-3"
                          style={{ fontSize: "1.85rem" }}
                        >
                          {ev.title}
                          <br />
                          <span className="sm-city">{ev.city_word}.</span>
                        </h3>
                      </div>
                    </div>
                    <hr className="sm-rule" />
                    <div className="flex flex-col gap-2">
                      <p className="sm-label" style={{ opacity: 0.75 }} data-testid={`event-when-${ev.slug}`}>
                        {prettyDate(ev.date)} · {ev.start_time}
                        {ev.end_time ? ` – ${ev.end_time}` : ""}
                      </p>
                      <p style={{ fontSize: "0.98rem", lineHeight: 1.6 }} data-testid={`event-where-${ev.slug}`}>
                        {ev.venue}, {ev.address}
                      </p>
                      <p className="sm-label" style={{ opacity: 0.75 }}>
                        Complimentary · Limited to {ev.capacity} guests
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-6 mt-2">
                      <a
                        href={GHL_SOCIAL_FORM_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="sm-btn sm-btn-primary"
                        data-testid={`event-rsvp-${ev.slug}`}
                      >
                        RSVP
                        <span aria-hidden>→</span>
                      </a>
                      <Link to={`/socials/${ev.slug}`} className="sm-arrow" data-testid={`event-view-${ev.slug}`}>
                        View evening →
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="sm-dark" style={{ padding: "2.5rem 1.5rem" }} data-testid="events-footer">
        <div className="max-w-[1240px] mx-auto text-center sm-label" style={{ opacity: 0.75 }}>
          © {new Date().getFullYear()} Shiva Murti Sacred Healing Arts
        </div>
      </footer>
    </div>
  );
};

export default EventsList;
